"use client";!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:zestlife-web":true});}catch(e){}}();
import React, { useState, useEffect, useCallback } from "react"
import get from "lodash/get"
import { resolveDisplayFirstName } from "../../../../lib/names"
import SignUpLoginHeader from "../../../pages/loginAndSignUp/shared/SignUpLoginHeader"
import StackSpaced from "../../../pages/loginAndSignUp/shared/StackSpaced"
import ColorButton from "../../../pages/loginAndSignUp/shared/ColorButton"
import SecondaryButton from "../../../pages/loginAndSignUp/shared/SecondaryButton"
import { useRouter, useSearchParams } from "next/navigation"
import TextField from "@mui/material/TextField"
import SecondaryLinkContainer from "../../../pages/loginAndSignUp/shared/SecondaryLinkContainer"
import updateSuccessMessage from "../../../../graphql/mutations/snackbarMessage/updateSuccessMessage"
import updateErrorMessage from "../../../../graphql/mutations/snackbarMessage/updateErrorMessage"
import updateWarningMessage from "../../../../graphql/mutations/snackbarMessage/updateWarningMessage"
import isEmail from "validator/lib/isEmail"
import {
  AUTH_ROLE_ZEST_ADMIN,
  AUTH_ROLE_ZEST_PROVIDER,
  AUTH_ROLE_ZEST_USER,
  USER_SURVEY_TYPE_POST_SIGNUP,
} from "../../../../lib/constants"
import getAllUrlParams from "../../../../lib/url/getAllUrlParams"
import { signUpEmailVar, isSigningInVar } from "../../../../graphql/reactiveVars"
import { useApolloClient, useReactiveVar } from "@apollo/client/react"
import Loader from "../../Loader"
import signInServerAction, {
  type SignInActionResult,
} from "../../../../lib/auth/signInServerAction"
import { getSession } from "next-auth/react"
import { resendSignUpCode } from "../../../../lib/auth/authActions"
import { safeCallbackUrl } from "../../../../lib/auth/safeCallbackUrl"

const EMAIL = "Email"
const PASSWORD = "Password"
const UNVERIFIED_EMAIL = "User is not confirmed."
const E2E_STUB_LOGIN_QUERY_PARAM = "stub_login"
const STUB_AUTH_ROLE_QUERY_PARAM = "stub_auth_role"
const STUB_LOGIN_ID_TOKEN = "stub-id-token"
const STUB_LOGIN_AUTH_ROLE = AUTH_ROLE_ZEST_USER
const STUB_LOGIN_REDIRECT_TO_LEADER = "false"
const STUB_LOGIN_FIRST_NAME = "nick+March27@getzestlife.com"
const STUB_LOGIN_PREFERRED_FIRST_NAME: string | null = null

function isE2EStubLoginEnabled(stubLoginParam: string | null): boolean {
  return stubLoginParam === "true"
}

function resolveStubAuthRole(stubAuthRoleParam: string | null): string {
  if (stubAuthRoleParam === AUTH_ROLE_ZEST_ADMIN) {
    return AUTH_ROLE_ZEST_ADMIN
  }
  if (stubAuthRoleParam === AUTH_ROLE_ZEST_PROVIDER) {
    return AUTH_ROLE_ZEST_PROVIDER
  }
  return STUB_LOGIN_AUTH_ROLE
}

function withStubAuthQuery(url: string, shouldBypassSignIn: boolean): string {
  if (!shouldBypassSignIn) {
    return url
  }
  const separator = url.includes("?") ? "&" : "?"
  return `${url}${separator}stub_auth=true`
}

export default function Login() {
  const router = useRouter()
  const apolloClient = useApolloClient()
  const [isEmailFocused, setIsEmailFocused] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const email = useReactiveVar(signUpEmailVar) as string | null

  const searchParams = useSearchParams()
  const allUrlParms = getAllUrlParams(searchParams)
  const redirectToLeaderParam = searchParams.get("redirect_to_leader")
  const stubLoginParam = searchParams.get(E2E_STUB_LOGIN_QUERY_PARAM)
  const stubAuthRoleParam = searchParams.get(STUB_AUTH_ROLE_QUERY_PARAM)
  const cohortIdParam = searchParams.get("cohort_id")
  const sendMessageParam = searchParams.get("send_message")
  const userIdParam = searchParams.get("user_id")
  const groupNameParam = searchParams.get("group_name")
  const callbackUrl = safeCallbackUrl(searchParams.get("callbackUrl"))
  const [password, setPassword] = useState("")

  const hrefForgotPassword = allUrlParms
    ? `/login/forgot-password?${allUrlParms}`
    : "/login/forgot-password"
  const hrefCreateAccount = allUrlParms ? `/signup?${allUrlParms}` : "/signup"

  const handleSignIn = useCallback(async () => {
    // Prevent double submission
    if (isSubmitting) {
      return
    }

    if (!email || !password) {
      updateErrorMessage("Please fill in email and password to login")
      return
    }

    setIsSubmitting(true)
    isSigningInVar(true) // Indicate that a sign-in attempt is in progress

    try {
      const shouldBypassSignIn = isE2EStubLoginEnabled(stubLoginParam)
      const response: SignInActionResult = shouldBypassSignIn
        ? { ok: true, response: null }
        : await signInServerAction(email, password)

      if (response.ok === false) {
        throw new Error(response.code)
      }

      // Add a small delay to ensure session is fully established on edge runtime
      // This prevents the "unexpected response from server" error
      await new Promise((resolve) => setTimeout(resolve, 150))

      // Refetch the session to ensure it's up to date
      const newSession = shouldBypassSignIn
        ? {
            user: {
              authRole: resolveStubAuthRole(stubAuthRoleParam),
              idToken: STUB_LOGIN_ID_TOKEN,
              redirectToLeader: STUB_LOGIN_REDIRECT_TO_LEADER,
            },
          }
        : await getSession()

      if (!newSession?.user) {
        throw new Error("Failed to retrieve session after login")
      }

      if (!shouldBypassSignIn) {
        await apolloClient.clearStore() // The destination loads fresh data after navigation
      }

      if (cohortIdParam) {
        window.location.href = withStubAuthQuery(
          `/checkout?cohort_id=${cohortIdParam}&redirected=login`,
          shouldBypassSignIn,
        )
        return
      }

      if (sendMessageParam) {
        window.location.href = withStubAuthQuery(
          `/messages?new_message=true&user_id=${userIdParam}&group_name=${groupNameParam}`,
          shouldBypassSignIn,
        )
        return
      }

      if (callbackUrl) {
        window.location.href = withStubAuthQuery(callbackUrl, shouldBypassSignIn)
        return
      }

      const authRole = newSession?.user?.authRole
      const [meData, surveyData] = shouldBypassSignIn
        ? [
            {
              me: {
                firstName: STUB_LOGIN_FIRST_NAME,
                preferredFirstName: STUB_LOGIN_PREFERRED_FIRST_NAME,
              },
            },
            { surveyCompleted: true },
          ]
        : await Promise.all([
            fetch("/edge/get-me", {
              headers: {
                "x-auth-token": newSession?.user?.idToken,
              },
            }).then(async (response) => response.json()),
            fetch(
              `/edge/onboarding/survey?userSurveyType=${USER_SURVEY_TYPE_POST_SIGNUP}`,
              {
                headers: {
                  "x-auth-token": newSession?.user?.idToken,
                },
              },
            ).then(async (response) => response.json()),
          ])
      const surveyCompleted = surveyData?.surveyCompleted
      const nameToUse = resolveDisplayFirstName(meData?.me) || null

      const redirectToLeaderDashboardCustomAttribute =
        newSession?.user?.redirectToLeader

      if (authRole == AUTH_ROLE_ZEST_ADMIN) {
        updateSuccessMessage(`Welcome back zesty admin!`)
        window.location.href = withStubAuthQuery("/admin", shouldBypassSignIn)
      }
      // else if (!surveyCompleted) {
      //   window.location.href = "/member-dashboard"
      //   // window.location.href = "/onboarding"
      // }
      else if (
        authRole == AUTH_ROLE_ZEST_PROVIDER ||
        redirectToLeaderDashboardCustomAttribute == "true" ||
        redirectToLeaderParam == "true"
      ) {
        updateSuccessMessage(
          `Welcome ${nameToUse ? ` ${nameToUse}` : " group leader"}!`,
        )
        window.location.href = withStubAuthQuery("/dashboard", shouldBypassSignIn)
      } else {
        updateSuccessMessage(`Welcome ${nameToUse ? ` ${nameToUse}` : ""}!`)
        window.location.href = withStubAuthQuery("/dashboard", shouldBypassSignIn)

        // window.location.href = "/member-dashboard"
      }
    } catch (error) {
      const errorMessage = get(error, "message", null)

      if (errorMessage === UNVERIFIED_EMAIL) {
        const result = await resendSignUpCode(email)
        if (result.success) {
          updateWarningMessage(
            "Please verify your email. Then login or reset password.",
          )
          router.push("/signup/confirm-email")
        } else {
          updateErrorMessage({
            message: get(result, "message", "Error occured. Please try again."),
            trackToSentry: true,
          })
        }
      } else {
        updateErrorMessage({
          message: get(error, "message", "Error occurred. Please try again."),
          trackToSentry: true,
          error,
        })
      }
    } finally {
      setIsSubmitting(false)
      isSigningInVar(false)
    }
  }, [
    email,
    password,
    router,
    redirectToLeaderParam,
    stubLoginParam,
    stubAuthRoleParam,
    apolloClient,
    cohortIdParam,
    sendMessageParam,
    userIdParam,
    groupNameParam,
    callbackUrl,
    isSubmitting,
  ])

  useEffect(() => {
    const listener = async (event: KeyboardEvent) => {
      if (event.code === "Enter" || event.code === "NumpadEnter") {
        event.preventDefault()
        handleSignIn()
      }
    }
    document.addEventListener("keydown", listener)
    return () => {
      document.removeEventListener("keydown", listener)
    }
  }, [handleSignIn])

  const isInvalid = (label: string, getMessage: boolean): string | boolean => {
    const userAnswer = label == EMAIL ? email : password

    // if email, make sure it is valid

    if (!userAnswer) {
      return false
    }

    if (label.toLowerCase().includes("email") && !validateEmail(userAnswer)) {
      if (getMessage) {
        return "Enter a valid email"
      }
      return true
    }
    return false
  }
  const validateEmail = (mail: string) => {
    return isEmail(mail)
  }

  const handleTextUpdate = (
    name: string,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (name == EMAIL) {
      signUpEmailVar(event.target.value)
    } else if (name == PASSWORD) {
      setPassword(event.target.value)
    }
  }

  return [
    <div
      className={"flex flex-col items-center justify-center w-full max-w-[500px]"}
      key="login-container"
    >
      <div className={``}>
        <SignUpLoginHeader text={"Log in to ZestLife"} />
      </div>

      <StackSpaced spacing={2}>
        <TextField
          id="outlined-disabled"
          key="email-login"
          label="Email"
          type="email"
          variant="outlined"
          error={!!isInvalid(EMAIL, false)}
          helperText={!isEmailFocused && isInvalid(EMAIL, true)}
          fullWidth={true}
          value={email || ""}
          onChange={(event) => {
            return handleTextUpdate(EMAIL, event)
          }}
          sx={{ maxWidth: "308px" }}
          autoFocus
          onFocus={() => setIsEmailFocused(true)}
          onBlur={() => setIsEmailFocused(false)}
        />

        <TextField
          key="pw-login"
          id="pw"
          label="Password"
          value={password}
          variant="outlined"
          type="password"
          fullWidth={true}
          onChange={(event) => {
            return handleTextUpdate(PASSWORD, event)
          }}
          sx={{ maxWidth: "308px" }}
        />
      </StackSpaced>
      <div className={"h-[20px]"} />
      <StackSpaced spacing={2}>
        <ColorButton
          onClick={handleSignIn}
          variant="contained"
          color="primary"
          key="sign-in-button"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Signing In..." : "Sign In"}
        </ColorButton>
        <SecondaryButton
          variant="contained"
          color="primary"
          href={hrefCreateAccount}
          key="sign-up-button"
        >
          Sign Up
        </SecondaryButton>
      </StackSpaced>
      <div className={"mt-[20px]"} key="forgot-pw-href">
        <SecondaryLinkContainer
          text={"Forgot your password?"}
          href={hrefForgotPassword}
        />
      </div>
    </div>,
    isSubmitting ? <Loader handleOnClick={() => {}} key="login-loader" /> : null,
  ]
}
